import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useWcagKeyboardEvents } from '@/features/conversational';
import { shuffleArray, throttle, tryFocus } from '@/utils';
import { useFormStore, useFormProps } from '@/features/form';
import HandleTabIndex from './HandleTabIndex';

import { getElementsHandleExtra } from '../utils';

export default function DropDown({ block, state, setValue }) {
	const selectRef = useRef(null);
	const itemsRef = useRef([]);
	const itemsObjectRef = useRef({});
	const { webView } = useFormProps();
	const { formType, contentDivRef } = useFormStore();
	const [listOpen, setListOpen] = useState(false);
	const [listHasFocus, setListHasFocus] = useState(false);
	const maxHeight = 250;

	//make it work with the title dropdown block
	const { elements, elementList } = useMemo(() => {
		if (block.options) {
			return {
				elements: block.options,
				elementList: Object.keys(block.options)
			};
		}
		const elements = getElementsHandleExtra({
			elements: block.properties?.elements,
			elements_extra: block.properties?.elements_extra
		});

		if (block.properties?.randomize) {
			return {
				elements,
				elementList: shuffleArray(
					Object.keys(elements),
					Object.keys(block.properties?.elements_extra ?? {}).length > 0
				)
			};
		}
		return {
			elements,
			elementList: Object.keys(elements)
		};
	}, [block?.properties, block?.options]);

	const toggleList = useCallback(
		throttle(force => {
			setListOpen(prev => (typeof force !== 'undefined' ? force : !prev));
			tryFocus(selectRef.current);
		}, 200),
		[]
	);

	const { onKeyDown } = useWcagKeyboardEvents({
		onSpaceEnter: e => {
			e.preventDefault();
			toggleList(true);
			setListHasFocus(true);
			tryFocus(itemsRef.current[0], 100);
		},
		onTabDown: () => {
			if (listOpen && !listHasFocus) {
				setListHasFocus(true);
			}
		},
		onTabUp: () => {
			if (listOpen && !listHasFocus) {
				toggleList(false);
				setListHasFocus(false);
			}
		},
		onArrowKeyUp: e => {
			if (!listHasFocus) {
				toggleList(true);
				e.preventDefault();
				setListHasFocus(true);
				tryFocus(itemsRef.current[0], 100);
			}
		},
		onArrowKeyDown: e => {
			if (!listHasFocus) {
				toggleList(true);
				e.preventDefault();
				setListHasFocus(true);
				tryFocus(itemsRef.current[0], 100);
			}
		},
		onHomeEndKeyPressed: e => {
			if (!listHasFocus) {
				const focusItem =
					e.key === 'Home'
						? itemsRef.current[0]
						: itemsRef.current[itemsRef.current.length - 1];
				e.preventDefault();
				e.stopPropagation();
				setListHasFocus(true);
				toggleList(true);
				tryFocus(focusItem, 100);
			}
		},
		onSingleKeyPressed(e) {
			const firstValue = Object.keys(itemsObjectRef.current).find(
				key => key.charAt(0).toLowerCase() === e.key
			);
			tryFocus(itemsObjectRef.current[firstValue]);
		}
	});

	useEffect(() => {
		const toggleListOuter = e => {
			if (!selectRef.current?.contains(e.target)) {
				toggleList(false);
			}
		};

		const clickAwayElement = formType === 'embed' ? document : contentDivRef.current;

		setTimeout(() => {
			if (!listOpen) {
				clickAwayElement?.removeEventListener('click', toggleListOuter);
			} else {
				clickAwayElement?.addEventListener('click', toggleListOuter);
			}
		});

		return () => {
			clickAwayElement?.removeEventListener('click', toggleListOuter);
		};
	}, [listOpen, formType, contentDivRef, toggleList]);

	const listRelative = listOpen && (formType !== 'embed' || webView);

	const selectedLabel =
		elements[
			elementList.filter(
				key =>
					elements[key]?.value === state.value || elements[key]?.label === state.value
			)[0]
		]?.label;

	return (
		<HandleTabIndex
			useTabIndexWhenOpen
			class={`nice-select ${listOpen ? 'open' : ''}`}
			onClick={() => toggleList()}
			role="combobox"
			aria-expanded={listOpen}
			aria-labelledby={`block-title-select-${block.id}`}
			ref={selectRef}
			onKeyDown={e => {
				onKeyDown(e);
			}}
		>
			<span class="current">
				{selectedLabel || block.properties?.placeholder || block.placeholder || ''}
			</span>
			<ul
				class="list"
				style={{
					maxHeight,
					...(listRelative && {
						position: 'relative'
					})
				}}
				role="listbox"
				aria-labelledby={`block-title-select-${block.id}`}
				aria-activedescendant={state.value}
				tabIndex={listOpen ? 0 : -1}
			>
				{elementList.map((key, index) => {
					const element = elements[key];

					const label = typeof element === 'string' ? element : element.label;
					const value =
						typeof element === 'string' ? element : element.value ?? element.label;

					const isLast = index === elementList.length - 1;

					return (
						<ListOption
							selectRef={selectRef}
							itemsRef={itemsRef}
							itemsObjectRef={itemsObjectRef}
							key={index}
							index={index}
							stateValue={state.value}
							label={label}
							value={value}
							elementKey={key}
							listOpen={listOpen}
							listHasFocus={listHasFocus}
							setValue={setValue}
							toggleList={toggleList}
							setListHasFocus={setListHasFocus}
							isLast={isLast}
						/>
					);
				})}
			</ul>
		</HandleTabIndex>
	);
}

function ListOption({
	index,
	elementKey,
	listOpen,
	stateValue,
	label,
	value,
	listHasFocus,
	isLast,
	itemsRef,
	itemsObjectRef,
	selectRef,
	setListHasFocus = () => {},
	toggleList = () => {},
	setValue = () => {}
}) {
	const { onKeyDown } = useWcagKeyboardEvents({
		onSpaceEnter: () => {
			if (listOpen && listHasFocus) {
				setValue(value);
				setListHasFocus(false);
				toggleList(false);
				tryFocus(selectRef.current, 100);
			}
		},
		onTabDown: () => {
			if (listOpen && listHasFocus) {
				setValue(value);
				setListHasFocus(false);
				toggleList(false);
			}
		},
		onArrowKeyUp: e => {
			if (listOpen && listHasFocus && index !== 0) {
				e.preventDefault();
				tryFocus(itemsRef.current[index - 1]);
			}
		},
		onArrowKeyDown: e => {
			if (listOpen && listHasFocus && !isLast) {
				e.preventDefault();
				tryFocus(itemsRef.current[index + 1]);
			}
		},
		onEscape: e => {
			e.stopPropagation();
			setListHasFocus(false);
			toggleList(false);
			tryFocus(selectRef.current, 100);
		}
	});

	return (
		<li
			tabIndex={listOpen ? 0 : -1}
			key={index}
			ref={node => {
				itemsRef.current[index] = node;
				itemsObjectRef.current[value] = node;
			}}
			data-value={value}
			role="option"
			aria-selected={value === stateValue}
			aria-hidden={!listOpen}
			id={`option_${elementKey}`}
			className={`option ${value === stateValue ? 'selected focus' : ''}`}
			onClick={e => {
				e.stopPropagation();
				setValue(value);
				setListHasFocus(false);
				toggleList();
			}}
			onKeyDown={e => {
				onKeyDown(e);
			}}
		>
			{label}
		</li>
	);
}
